import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import moment from 'moment';
import { useHistory, useParams } from 'react-router';

import Input from '../../../components/Input';
import Loader from '../../../components/Loader';
import Select from '../../../components/Select';
import CustomCheckbox from '../../../components/Checkbox';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';

import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';

import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import { SelectProps } from '../../Records/List/dto';
import { User } from '../../../models/user';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
}));

const ClosureForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [record, setRecord] = useState<Record | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const [users, setUsers] = useState<SelectProps[]>([]);
  const [date, setDate] = useState(new Date());
  const [authDate, setAuthDate] = useState<Date | null>(null);
  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);

        if (recordResponse?.data) {
          setRecord(recordResponse.data);
        }

        const usersResponse = await api.current?.get('/users');

        if (usersResponse?.data) {
          setUsers(
            usersResponse.data.map((user: User) => ({
              value: user.id,
              label: `${user.firstName} ${user.lastName}`,
            })),
          );
        }

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          if (formResponse?.data) {
            setDate(
              formResponse?.data.answers.date
                ? moment(formResponse?.data.answers.date).toDate()
                : new Date(),
            );

            setAuthDate(
              formResponse?.data.answers.authDate
                ? moment(formResponse?.data.answers.authDate).toDate()
                : null,
            );
          }
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });
        data.professional =
          users.find((u) => u.value === data.professional_id)?.label || '';

        data.date = moment(date).format('YYYY-MM-DD');
        if (authDate) data.authDate = moment(authDate).format('YYYY-MM-DD');

        const formRequest = {
          type: 'closure',
          title: 'Encerramento do Atendimento',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        history.push(`/prontuarios/${id}`);
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else if (error.response && error.response.data) {
          switch (error.response.data.message) {
            case 'Form already sent':
              setAlert({
                isActive: true,
                type: 'warning',
                message: 'Formulário em análise, solicite a revisão.',
              });
              break;
            default:
              setAlert({
                isActive: true,
                type: 'error',
                message: 'Erro ao enviar encerramento.',
              });
              break;
          }
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar encerramento.',
          });
        }
      }
    },
    [api, record, history, formId, date, authDate, id, users, signatures],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers || { record: record?.number }}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              NOTA DE ENCERRAMENTO DO ATENDIMENTO
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Select
                name="professional_id"
                defaultValue={
                  form?.answers
                    ? {
                        value: form?.answers.professional_id,
                        label: users.find(
                          (user) =>
                            user.value === form?.answers.professional_id,
                        )?.label,
                      }
                    : null
                }
                isMulti={false}
                placeholder="Nome do Terapeuta"
                options={users}
              />
            </Grid>

            <Grid item xs={2} />

            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Data do encerramento"
                  inputFormat="dd/MM/yyyy"
                  value={date}
                  onChange={(d) => setDate(d as Date)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <FormLabel className={classes.formTitles}>
                O paciente encerrou atendimento por:
              </FormLabel>
            </Grid>

            <Grid item xs={12}>
              <CustomCheckbox
                name="clinic"
                value="clinic"
                label="Alta clínica"
              />
            </Grid>

            <Grid item xs={12}>
              <CustomCheckbox
                name="combined"
                value="combined"
                label="Alta combinada"
              />
            </Grid>

            <Grid item xs={12}>
              <CustomCheckbox
                name="interruption"
                value="interruption"
                label="Interrupção"
              />
            </Grid>

            <Grid item xs={12}>
              <CustomCheckbox name="abandon" value="abandon" label="Abandono" />
            </Grid>

            <Grid item xs={12}>
              <CustomCheckbox
                name="forwarding"
                value="forwarding"
                label="Encaminhamento para consultório do terapeuta"
              />
            </Grid>

            <Grid item xs={12}>
              <CustomCheckbox name="others" value="others" label="Outros" />
            </Grid>

            <Grid item xs={12}>
              <Input
                id="motive"
                isMultiline
                rows={5}
                label="Explique brevemente as circunstâncias do encerramento do atendimento"
                name="motive"
              />
            </Grid>
          </Grid>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  *mediante aprovação da direção de ambulatório
                </FormLabel>
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            {/* <Divider className={classes.divider} /> */}
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.goBack()}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default ClosureForm;
